import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiCalendar, FiMapPin, FiBriefcase, FiAward } from 'react-icons/fi';

const Experience = ({ hideTitle = false }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const experiences = [
    {
      title: 'Sr. SQA Engineer',
      company: 'Exact Solution Electronics',
      location: 'Warsaw, Mazowieckie, Poland · Remote',
      period: 'Mar 2026 - Present',
      type: 'Full-time',
      achievements: [
        'Performing end-to-end manual testing of ExactFlow, a complex multi-marketplace e-commerce platform',
        'Validating Product and Order modules, including product listings, stock synchronization, product associations, and order processing workflows',
        'Verifying high-volume order flows across multiple marketplaces, ensuring accuracy of product mapping, invoices, shipment details, and timestamps',
        'Conducting functional, integration, regression, and edge-case testing for listings, stock management, reservations, invoicing, WMS workflows including picking, packing, stocktake, inventory reconciliation, and shipment preparation processes',
        'Collaborating with QA and development teams to validate integrations, new features, and production releases',
      ],
      technologies: ['Manual Testing', 'Functional Testing', 'Regression Testing', 'Integration Testing', 'WMS', 'E-commerce']
    },
    {
      title: 'SQA Engineer',
      company: 'BugVerse',
      location: 'Lahore, Pakistan',
      period: 'Oct 2025 - Mar 2026',
      type: 'Full-time',
      achievements: [
        'Owned QA execution for healthcare and AI-based web applications; validated features against requirements',
        'Designed and maintained Cypress automated tests using standard automation design patterns',
        'Performed functional and regression testing; reported and verified issues using JIRA',
        'Executed API testing using Postman and HTTP methods (GET, POST, PUT, DELETE)',
        'Collaborated with development teams and stakeholders to improve overall product quality',
      ],
      technologies: ['Cypress', 'JavaScript', 'Postman', 'REST APIs', 'JIRA', 'Regression Testing']
    },
    {
      title: 'SQA Engineer',
      company: 'UltraCodes',
      location: 'Lahore, Pakistan',
      period: 'Jul 2023 - Oct 2025',
      type: 'Full-time',
      achievements: [
        'Delivered QA for multiple SaaS digital products across staging and production environments',
        'Implemented automated tests with Cypress and JavaScript for critical user flows',
        'Supported monitoring-focused and CRM features by validating UI accuracy and real-time data',
        'Worked closely with developers to triage defects and confirm fixes',
      ],
      technologies: ['Cypress', 'JavaScript', 'Manual Testing', 'Regression', 'Sanity', 'Exploratory']
    },
    {
      title: 'SQA Engineer',
      company: 'Pikes Soft',
      location: 'Lahore, Pakistan',
      period: 'Dec 2021 - Mar 2023',
      type: 'Full-time',
      achievements: [
        'Executed manual testing for web and mobile applications across healthcare and e-commerce domains',
        'Created test cases and test plans; performed functional, integration, and UAT testing',
        'Managed defect lifecycle and maintained QA documentation',
      ],
      technologies: ['Manual Testing', 'Functional', 'Integration', 'UAT', 'E-commerce', 'Healthcare']
    },
    {
      title: 'SQA Trainee',
      company: 'Programmer Force',
      location: 'Lahore, Pakistan',
      period: 'Aug 2021 - Nov 2021',
      type: 'Internship',
      achievements: [
        'Gained hands-on experience in STLC/SDLC, manual testing, defect reporting, and Selenium WebDriver basics',
      ],
      technologies: ['Manual Testing', 'Selenium', 'Python', 'STLC/SDLC']
    },
  ];

  return (
    <section id="experience" ref={ref} className="section-padding bg-white dark:bg-dark-100 transition-colors duration-300">
      <div className="container-custom">
        {!hideTitle && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-secondary-800 dark:text-white mb-4">
            Work <span className="gradient-text">Experience</span>
          </h2>
          <p className="text-lg text-secondary-600 dark:text-gray-100 max-w-2xl mx-auto">
            My professional journey in SQA, with a focus on manual and automated testing, test automation with Cypress, and cross-functional collaboration.
          </p>
        </motion.div>
        )}

        <div className="space-y-8">
          {experiences.map((exp, index) => (
            <motion.div
              key={`${exp.company}-${exp.title}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.2 }}
              className="relative"
            >
              {/* Timeline connector aligned with icon */}
              {index < experiences.length - 1 && (
                <div className="absolute top-6 left-[24px] w-0.5 h-full bg-gradient-to-b from-primary-500 to-transparent z-0" />
              )}

              <div className="flex flex-col md:flex-row gap-6">
                {/* Timeline dot */}
                <div className="flex-shrink-0 relative z-10" style={{ width: '48px' }}>
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-600 rounded-full flex items-center justify-center shadow-lg">
                    <FiBriefcase className="w-6 h-6 text-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-dark-200 dark:to-dark-300 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 z-10">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-secondary-800 dark:text-white mb-1">
                        {exp.title}
                      </h3>
                      <p className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">
                        {exp.company}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-secondary-600 dark:text-gray-100 mt-2 md:mt-0">
                      <div className="flex items-center gap-1">
                        <FiCalendar className="w-4 h-4" />
                        <span>{exp.period}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FiMapPin className="w-4 h-4" />
                        <span>{exp.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FiAward className="w-4 h-4" />
                        <span>{exp.type}</span>
                      </div>
                    </div>
                  </div>

                  {/* Achievements */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-secondary-700 dark:text-gray-100 mb-3">Key Achievements:</h4>
                    <ul className="space-y-2">
                      {exp.achievements.map((achievement, achievementIndex) => (
                        <motion.li
                          key={achievementIndex}
                          initial={{ opacity: 0, x: 20 }}
                          animate={inView ? { opacity: 1, x: 0 } : {}}
                          transition={{ delay: (index * 0.2) + (achievementIndex * 0.1) }}
                          className="flex items-start space-x-2 text-secondary-600 dark:text-gray-100"
                        >
                          <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm">{achievement}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Technologies */}
                  <div>
                    <h4 className="font-semibold text-secondary-700 dark:text-gray-100 mb-2">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.map((tech, techIndex) => (
                        <motion.span
                          key={tech}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={inView ? { opacity: 1, scale: 1 } : {}}
                          transition={{ delay: (index * 0.2) + (techIndex * 0.05) }}
                          className="px-3 py-1 bg-white dark:bg-dark-300 text-primary-600 dark:text-primary-400 text-xs font-medium rounded-full border border-primary-200 dark:border-dark-300"
                        >
                          {tech}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
